import React, { ReactElement, SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import RadioButtons from 'commons/src/components/input/Radio';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import ThresholdSetting from 'commons/src/components/thresholds/ThresholdSetting';
import { sensorFullNameKey, sensorUnits } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import { CustomOptions, ThresholdOptions } from 'commons/src/models/customThresholdModels';
import { toErrorTypeWithMessage } from 'commons/src/sagas/isErrorType';
import { useUpdateThresholdsMutation, ThresholdConfigSensor } from '../../../api';
import styles from './SensorThresholdMgmt.module.scss';

export type Props = {
    buttonText: string;
    sensorType: SensorTypes;
    thresholds: {
        customOptions?: CustomOptions;
        ranges: ThresholdRange[];
        unit: keyof typeof sensorUnits;
        thresholdOptions: ThresholdOptions;
    };
    thresholdScope: ThresholdsResourceScope;
    parentThresholdScope?: ThresholdsResourceScope;
    id: string | undefined;
};
const SensorThresholdMgmt = ({
    sensorType,
    thresholds,
    thresholdScope,
    buttonText,
    parentThresholdScope,
    id,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isSaved, setIsSaved] = useState(false);
    const [selectedValues, setSelectedValues] = useState<number[]>([]);
    const [selectedScope, setSelectedThresholdScope] = useState(
        thresholds.thresholdOptions.activeScope || thresholdScope
    );

    const changeScopeSelection = (e: SyntheticEvent<HTMLInputElement>): void => {
        const { value } = e.currentTarget;
        setSelectedThresholdScope(value as ThresholdsResourceScope);
    };
    const goToParentThresholds = (): void => {
        const linkToParentThresholds = 'settings/thresholds';
        navigate(linkToParentThresholds);
    };
    const radioOptions = [
        {
            value: parentThresholdScope,
            label: `ThresholdSettings.Default_${parentThresholdScope}`,
            customStyle: styles.parentRadioButton,
            buttonChildren: (
                <div className={styles.editLink}>
                    <TertiaryButton onClick={goToParentThresholds} title="Edit" size="medium" />
                </div>
            ),
        },
        { value: thresholdScope, label: `ThresholdSettings.Custom_${thresholdScope}` },
    ];
    const thresholdOptions = useMemo(() => {
        const { options } = thresholds.thresholdOptions;
        return (selectedScope && options?.[selectedScope]) || [];
    }, [selectedScope, thresholds.thresholdOptions.options]);

    const [updateThresholds, { isLoading }] = useUpdateThresholdsMutation();

    const handleOnSave = async (sensorValue: ThresholdConfigSensor): Promise<void> => {
        if (!id) return;
        const request = {
            resourceScope: thresholdScope,
            id,
            sensors: [sensorValue],
        };
        try {
            await updateThresholds(request).unwrap();
            setIsSaved(true);
            dispatch(displayAlertBoxSagaAction('CustomThresholds.UpdateSuccess', false, true));
        } catch (err) {
            dispatch(displayAlertBoxSagaAction(toErrorTypeWithMessage(err).message, true, true));
        }
    };
    const handleSetSelectedValues = (values: number[]): void => {
        setSelectedValues(values);
        if (isSaved) setIsSaved(false);
    };

    return (
        <div>
            <SectionHeader
                headerType={4}
                headerName={sensorFullNameKey(sensorType)}
                leadingIcon={<SensorIcon sensor={sensorType} />}
                key={`thresholdHeader${sensorType}`}
            />
            <p className={styles.description}>{txt(`CustomThresholds.${sensorType}Description`)}</p>
            {parentThresholdScope && (
                <div className={styles.radioButtons}>
                    <RadioButtons
                        row
                        buttons={radioOptions}
                        onChange={changeScopeSelection}
                        labelId={`thresholdHeader${sensorType}`}
                        selectorName="scope"
                        value={selectedScope}
                    />
                </div>
            )}
            <ThresholdSetting
                sensorType={sensorType}
                ranges={thresholds.ranges}
                thresholdOptions={thresholdOptions}
                customOptions={thresholds.customOptions}
                selectedValues={selectedValues}
                setSelectedValues={handleSetSelectedValues}
                isParentScope={selectedScope === parentThresholdScope}
                scopeModified={selectedScope !== thresholds.thresholdOptions.activeScope}
                buttonText={buttonText}
                loading={isLoading}
                onSave={handleOnSave}
                isSaved={isSaved}
            />
        </div>
    );
};

export default SensorThresholdMgmt;
